import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  INSURANCE_COMMISSION_BASIS_OPTIONS,
  commissionBasisLabelKey,
  defaultCommissionRulesJson,
  getCommissionPlanRulesFields
} from './insurance-commission-plan-rules-schemas';
import {
  CreateInsuranceCommissionPlanPayload,
  InsuranceApiService,
  InsuranceCommissionPlanListItem
} from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-commission-plans',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    StructuredJsonFormComponent,
    PartySelectorComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    CheckboxModule,
    SelectModule
  ],
  template: `
    <ain-page [title]="t('insurance.commissions.plans.title')" [description]="t('insurance.commissions.plans.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.commissions.plans.help.page.title'"
        [descriptionKey]="'insurance.commissions.plans.help.page.description'"
        [bulletKeys]="[
          'insurance.commissions.plans.help.page.bullets.scope',
          'insurance.commissions.plans.help.page.bullets.usage',
          'insurance.commissions.plans.help.page.bullets.audit'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.commissions.actions.createPlan')" (click)="openCreateDialog()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.commissions.plans.help.listFilters.title'"
                [descriptionKey]="'insurance.commissions.plans.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.commissions.plans.help.listFilters.bullets.search',
                  'insurance.commissions.plans.help.listFilters.bullets.selection',
                  'insurance.commissions.plans.help.listFilters.bullets.status'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.commissions.plans.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.commissions.plans.filters.status') }}</span>
                <p-select
                  [options]="statusFilterOptions"
                  optionValue="value"
                  [(ngModel)]="statusFilter"
                  name="statusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.commissions.plans.states.empty')" [message]="t('insurance.commissions.plans.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('insurance.commissions.plans.fields.code') }}</th>
                  <th>{{ t('insurance.commissions.plans.fields.name') }}</th>
                  <th>{{ t('insurance.commissions.plans.fields.basis') }}</th>
                  <th>{{ t('insurance.commissions.plans.fields.clawbackMonths') }}</th>
                  <th>{{ t('columns.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.code }}</button></td>
                  <td>{{ row.name }}</td>
                  <td>{{ t(basisLabelKey(row.basis)) }}</td>
                  <td>{{ row.clawbackMonths ?? '-' }}</td>
                  <td>{{ row.isActive ? t('common.enabled') : t('common.disabled') }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }

      <p-dialog
        [header]="t('insurance.commissions.planDetail.createTitle')"
        [modal]="true"
        [(visible)]="createDialogVisible"
        [style]="{ width: '720px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span>{{ t('insurance.commissions.planDetail.fields.legalEntity') }}</span>
            <ain-party-selector [(ngModel)]="createForm.legalEntityId" name="createLegalEntityId" />
          </label>
          <label>
            <span>{{ t('insurance.commissions.planDetail.fields.code') }}</span>
            <input pInputText [(ngModel)]="createForm.code" name="createCode" />
          </label>
          <label>
            <span>{{ t('insurance.commissions.planDetail.fields.name') }}</span>
            <input pInputText [(ngModel)]="createForm.name" name="createName" />
          </label>
          <label>
            <span>{{ t('insurance.commissions.planDetail.fields.basis') }}</span>
            <p-select
              [options]="basisOptions"
              optionValue="value"
              [(ngModel)]="createForm.basis"
              name="createBasis"
              appendTo="body"
              (ngModelChange)="onCreateBasisChanged($event)">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
            </p-select>
          </label>
          <label>
            <span>{{ t('insurance.commissions.planDetail.fields.clawbackMonths') }}</span>
            <p-inputNumber [(ngModel)]="createForm.clawbackMonths" name="createClawbackMonths" [min]="0" [max]="120" />
          </label>
          <label class="checkbox">
            <p-checkbox binary="true" [(ngModel)]="createForm.isActive" name="createIsActive" />
            <span>{{ t('insurance.commissions.planDetail.fields.isActive') }}</span>
          </label>
          @if (createForm.basis !== 'Tiered') {
            <div class="wide">
              <span class="field-label">{{ t('insurance.commissions.planDetail.fields.rules') }}</span>
              <ain-structured-json-form [fields]="createRulesFields()" [(payloadJson)]="createForm.rulesJson" />
            </div>
          } @else {
            <p class="wide hint">{{ t('insurance.commissions.plans.createTieredHint') }}</p>
          }
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="creating()" (click)="saveCreate()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card{border:1px solid #e5e7eb;border-radius:12px;background:#fff;padding:12px;display:grid;gap:10px}
    .panel{display:grid;gap:10px}
    .toolbar{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
    .filters{border:1px solid #e5e7eb;border-radius:10px;padding:8px 10px;background:#f9fafb}
    .filters summary{display:flex;align-items:center;gap:8px}
    .filter-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:8px}
    .filter-grid label{display:grid;gap:6px}
    .dialog-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
    .dialog-grid label{display:grid;gap:6px}
    .dialog-grid .wide{grid-column:1/-1}
    .field-label{color:#475569;font-size:12px;font-weight:600}
    .checkbox{display:flex;gap:8px;align-items:center}
    .hint{color:#64748b;margin:0}
    .link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}
    .error{color:#b91c1c;margin:0}
    @media (max-width: 900px){ .filter-grid,.dialog-grid{grid-template-columns:1fr} }
  `]
})
export class InsuranceCommissionPlansComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly creating = signal(false);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InsuranceCommissionPlanListItem[]>([]);
  readonly selectedRows = signal<InsuranceCommissionPlanListItem[]>([]);
  readonly search = signal('');
  statusFilter: boolean | null = null;
  createDialogVisible = false;

  readonly basisOptions = INSURANCE_COMMISSION_BASIS_OPTIONS;
  readonly statusFilterOptions = [
    { value: true, labelKey: 'common.enabled' },
    { value: false, labelKey: 'common.disabled' }
  ];

  createRulesFields() {
    return getCommissionPlanRulesFields(this.createForm.basis);
  }

  createForm: CreateInsuranceCommissionPlanPayload = this.defaultCreateForm();

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.listCommissionPlans().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.error.set('');
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceCommissionPlanListItem[] {
    const value = this.search().trim().toLowerCase();
    return this.rows()
      .filter(item => this.statusFilter === null || item.isActive === this.statusFilter)
      .filter(item =>
        !value ||
        item.code.toLowerCase().includes(value) ||
        item.name.toLowerCase().includes(value) ||
        this.t(commissionBasisLabelKey(item.basis)).toLowerCase().includes(value));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
  }

  onSelectionChange(selection: InsuranceCommissionPlanListItem[] | InsuranceCommissionPlanListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/commission-plans', id]);
  }

  openCreateDialog(): void {
    this.createForm = this.defaultCreateForm();
    this.createDialogVisible = true;
  }

  onCreateBasisChanged(basis: string): void {
    this.createForm.rulesJson = defaultCommissionRulesJson(basis);
  }

  saveCreate(): void {
    this.creating.set(true);
    this.error.set('');
    this.api.createCommissionPlan(this.createForm).subscribe({
      next: item => {
        this.creating.set(false);
        this.createDialogVisible = false;
        void this.router.navigate(['/insurance/commission-plans', item.id]);
      },
      error: () => {
        this.creating.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  basisLabelKey(basis: string): string {
    return commissionBasisLabelKey(basis);
  }

  private defaultCreateForm(): CreateInsuranceCommissionPlanPayload {
    return {
      legalEntityId: '',
      code: '',
      name: '',
      basis: 'PercentOfPremium',
      rulesJson: defaultCommissionRulesJson('PercentOfPremium'),
      clawbackMonths: 6,
      isActive: true
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
